/* eslint-disable @typescript-eslint/no-explicit-any */

import type { AxiosResponse } from 'axios';
import { call, delay, put } from 'redux-saga/effects';

import api from '../../api/api';
import { errorHandling } from '../../api/errorHandling';
import { mapSuccessResponseToState } from '../../api/responseHandling';
import {
	getStoragePrefix,
	readCachedStructure,
	writeCachedStructure,
} from '../../hooks/useConfigStorage';
import i18n from '../../utils/i18n';
import { getApiVersion } from '../apiVersion';
import { resolveSliceActions } from '../reduxSlicesList';

const EXCEL_EXPORT_TIMEOUT_MS = 120000;

export const buildTablePath = (table: string, version: number) => {
	const [base, ...rest] = table.split('/');
	const baseCap = base.charAt(0).toUpperCase() + base.slice(1);
	const sub = rest.length ? `/${rest.join('/')}` : '';
	return { path: `/${baseCap}/v${version}${sub}` };
};

export function* getTable(props: any) {
	const slice = resolveSliceActions(props.payload.table);
	if (!slice) {
		console.error(
			`No Redux slice registered for table "${props.payload.table}"`,
		);
		return;
	}
	try {
		const { table, url, ...rest } = props.payload;
		const version = getApiVersion(table, 'getTable');
		const { path } = buildTablePath(table, version);
		const response: AxiosResponse<any[]> = yield call(
			api.post,
			url || path,
			rest,
		);
		yield put(slice.getTableSuccess(mapSuccessResponseToState(response)));
		yield delay(0);
		yield put(slice.getTableFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.getTableFailure);
	}
}

export function* getItem(props: any) {
	const slice = resolveSliceActions(props.payload.table);
	if (!slice) {
		console.error(
			`No Redux slice registered for table "${props.payload.table}"`,
		);
		return;
	}
	try {
		const { table, id } = props.payload;
		const version = getApiVersion(table, 'getItem');
		const { path } = buildTablePath(table, version);
		const response: AxiosResponse<any[]> = yield call(api.get, `${path}/${id}`);

		yield put(slice.getItemSuccess(mapSuccessResponseToState(response)));
		yield delay(0);
		yield put(slice.getItemFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.getItemFailure);
	}
}

export function* createItem(props: any) {
	const slice = resolveSliceActions(props.payload.table);
	if (!slice) {
		console.error(
			`No Redux slice registered for table "${props.payload.table}"`,
		);
		return;
	}
	try {
		const { table } = props.payload;
		const version = getApiVersion(table, 'createItem');
		const { path } = buildTablePath(table, version);
		const response: AxiosResponse<any[]> = yield call(
			api.post,
			`${path}/create`,
			props.payload.data,
		);

		yield put(
			slice.createItemSuccess(mapSuccessResponseToState(response, 'created')),
		);
		yield delay(0);
		yield put(slice.createItemFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.createItemFailure);
	}
}

export function* updateItem(props: any) {
	const slice = resolveSliceActions(props.payload.table);
	if (!slice) {
		console.error(
			`No Redux slice registered for table "${props.payload.table}"`,
		);
		return;
	}
	try {
		const { table, id, data, isPut } = props.payload;
		const version = getApiVersion(table, 'updateItem');
		const { path } = buildTablePath(table, version);

		const response: AxiosResponse<any[]> = yield call(
			isPut ? api.put : api.patch,
			`${path}/${isPut ? 'update' : 'patch'}/${id}`,
			data,
		);

		yield put(
			slice.updateItemSuccess(mapSuccessResponseToState(response, 'updated')),
		);
		yield delay(0);
		yield put(slice.updateItemFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.updateItemFailure);
	}
}

export function* deleteItems(props: any) {
	const slice = resolveSliceActions(props.payload.table);
	if (!slice) {
		console.error(
			`No Redux slice registered for table "${props.payload.table}"`,
		);
		return;
	}
	try {
		const { table, ids } = props.payload;
		if (!Array.isArray(ids) || ids.length === 0) {
			yield put(slice.deleteItemsFinish());
			return;
		}
		// No table exposes a bulk hard-delete endpoint (Results' /abort only
		// flips State=Aborted), so delete row by row — inside this one saga,
		// so a bulk selection is never split into N dispatches that takeLatest
		// would cancel (cancelled sagas skip their catch and swallow failures).
		const version = getApiVersion(table, 'deleteItem');
		const { path } = buildTablePath(table, version);
		let response: AxiosResponse<any[]> | null = null;
		// Every id is attempted even after one fails. Each delete commits on
		// its own, so stopping at the first error left the selection half
		// deleted with nothing telling the user which rows actually went.
		const failedIds: Array<string | number> = [];
		let firstError: unknown = null;
		for (const id of ids) {
			try {
				response = yield call(api.del, `${path}/delete/${id}`, {});
			} catch (error: unknown) {
				failedIds.push(id);
				firstError ??= error;
			}
		}

		if (failedIds.length === ids.length) {
			throw firstError;
		}

		if (failedIds.length) {
			// The success action drives the grid refresh (rows that went are
			// gone) and carries the failed ids so the selection keeps only
			// those; the failure action that follows shows the reason.
			const deleted = ids.length - failedIds.length;
			yield put(
				slice.deleteItemsSuccess({
					message: `${i18n.t('toasts.deletedPartial')} (${deleted}/${ids.length})`,
					type: 'warning',
					failedIds,
				}),
			);
			yield delay(0);
			yield* errorHandling(firstError, slice.deleteItemsFailure);
			return;
		}

		// Plural wording: this path always removed a selection, and the singular
		// "Record deleted" read as if only one row went.
		yield put(
			slice.deleteItemsSuccess(
				mapSuccessResponseToState(
					response as AxiosResponse<any[]>,
					'deletedMany',
				),
			),
		);
		yield delay(0);
		yield put(slice.deleteItemsFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.deleteItemsFailure);
	}
}

export function* deleteItem(props: any) {
	const slice = resolveSliceActions(props.payload.table);
	if (!slice) {
		console.error(
			`No Redux slice registered for table "${props.payload.table}"`,
		);
		return;
	}
	try {
		const { table, id } = props.payload;
		const version = getApiVersion(table, 'deleteItem');
		const { path } = buildTablePath(table, version);
		const response: AxiosResponse<any[]> = yield call(
			api.del,
			`${path}/delete/${id}`,
			{},
		);

		yield put(
			slice.deleteItemSuccess(mapSuccessResponseToState(response, 'deleted')),
		);
		yield delay(0);
		yield put(slice.deleteItemFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.deleteItemFailure);
	}
}

// Tables whose cached structure was already revalidated against the API this
// page-load. The cached copy paints instantly, then one background fetch per
// table+lang catches backend structure changes without waiting out the 24h
// TTL — so a plain reload after a Rest.Api deploy shows the new structure.
const revalidatedStructures = new Set<string>();

export const resetStructureRevalidationForTests = () => {
	revalidatedStructures.clear();
};

export function* getTableStructure(props: any) {
	const slice = resolveSliceActions(props.payload.table);
	if (!slice) {
		console.error(
			`No Redux slice registered for table "${props.payload.table}"`,
		);
		return;
	}
	try {
		const { table } = props.payload;
		const version = getApiVersion(table, 'getTableStructure');
		const { path } = buildTablePath(table, version);
		const lang = i18n.language ? i18n.language : 'lt';
		// Cache key must be the full page name (matches the writer) — reading
		// under the capitalized base ('Results' vs 'results') never hit
		// localStorage.
		const structureObj = readCachedStructure(table, lang) || [];
		// The cache key follows the user cookie at call time and the cookie is
		// shared across tabs. Remember which database this fetch is for, so a
		// response landing after another tab switched database is not filed
		// under the new one (24h TTL, shows up as empty edit forms).
		const scope = getStoragePrefix();
		if (structureObj.length === 0) {
			const response: AxiosResponse<any[]> = yield call(
				api.get,
				`${path}/structure?lang=${lang}`,
			);

			// Cache is written only on a real fetch — writing on the cached
			// branch too would keep refreshing the timestamp and the 24h TTL
			// would never expire for daily users.
			if (getStoragePrefix() === scope) {
				writeCachedStructure(table, lang, response?.data || []);
			}
			yield put(
				slice.getTableStructureSuccess(mapSuccessResponseToState(response)),
			);
			yield delay(0);
			yield put(slice.getTableStructureFinish());
		} else {
			yield put(
				slice.getTableStructureSuccess({
					data: structureObj,
				}),
			);
			yield delay(0);
			yield put(slice.getTableStructureFinish());

			const revalidationKey = `${table}:${lang}`;
			if (!revalidatedStructures.has(revalidationKey)) {
				revalidatedStructures.add(revalidationKey);
				try {
					const response: AxiosResponse<any[]> = yield call(
						api.get,
						`${path}/structure?lang=${lang}`,
					);
					const fresh = response?.data || [];
					// Re-dispatch only on a real change: a content-equal structure
					// with a new identity would re-run XTable's fetch effect and
					// double the grid POST (see useTableFetch deps).
					if (
						fresh.length > 0 &&
						JSON.stringify(fresh) !== JSON.stringify(structureObj)
					) {
						if (getStoragePrefix() === scope) {
							writeCachedStructure(table, lang, fresh);
						}
						yield put(
							slice.getTableStructureSuccess(
								mapSuccessResponseToState(response),
							),
						);
						yield delay(0);
						yield put(slice.getTableStructureFinish());
					}
				} catch {
					// Background revalidation only — the grid already rendered from
					// cache, so a failed refresh must not toast or flip failure state.
				}
			}
		}
	} catch (error: unknown) {
		yield* errorHandling(error, slice.getTableStructureFailure);
	}
}

export function* getItemDocuments(props: any) {
	const { table, id } = props.payload;
	const slice = resolveSliceActions(table);
	if (!slice) {
		console.error(`No Redux slice registered for table "${table}"`);
		return;
	}
	const version = getApiVersion(table, 'getItemDocuments');
	const { path } = buildTablePath(table, version);

	try {
		const response: AxiosResponse<any[]> = yield call(
			api.get,
			`${path}/${id}/documents`,
		);

		yield put(
			slice.getItemDocumentsSuccess(mapSuccessResponseToState(response)),
		);
		yield delay(0);
		yield put(slice.getItemDocumentsFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.getItemDocumentsFailure);
	}
}

export function* uploadItemDocuments(props: any) {
	const { table, id, files } = props.payload;
	const slice = resolveSliceActions(table);
	if (!slice) {
		console.error(`No Redux slice registered for table "${table}"`);
		return;
	}
	const version = getApiVersion(table, 'uploadItemDocuments');
	const { path } = buildTablePath(table, version);

	try {
		const formData = new FormData();
		Array.from(files).forEach((f: any, i: number) => {
			formData.append(`uploads[${i}].File`, f);
			formData.append(`uploads[${i}].Name`, f.name || 'noname');
		});

		const response: AxiosResponse<any[]> = yield call(
			api.post,
			`${path}/${id}/documents/upload`,
			formData,
		);

		yield put(
			slice.uploadItemDocumentsSuccess(
				mapSuccessResponseToState(response, 'documentsUploaded'),
			),
		);
		yield delay(0);
		yield put(slice.uploadItemDocumentsFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.uploadItemDocumentsFailure);
	}
}

export function* deleteItemDocument(props: any) {
	const { table, id, documentId } = props.payload;
	const slice = resolveSliceActions(table);
	if (!slice) {
		console.error(`No Redux slice registered for table "${table}"`);
		return;
	}
	const version = getApiVersion(table, 'deleteItemDocument');
	const { path } = buildTablePath(table, version);
	try {
		const response: AxiosResponse<any[]> = yield call(
			api.del,
			`${path}/${id}/documents/delete/${documentId}`,
			{},
		);

		yield put(
			slice.deleteItemDocumentSuccess(
				mapSuccessResponseToState(response, 'documentDeleted'),
			),
		);
		yield delay(0);
		yield put(slice.deleteItemDocumentFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.deleteItemDocumentFailure);
	}
}

export function* getTableExcel(props: any) {
	const slice = resolveSliceActions(props.payload.table);
	if (!slice) {
		console.error(
			`No Redux slice registered for table "${props.payload.table}"`,
		);
		return;
	}
	try {
		const { table, ...rest } = props.payload;
		const version = getApiVersion(table, 'getTableExcel');
		const { path } = buildTablePath(table, version);
		// The backend binds lang from the query string ([FromQuery], default
		// en-US) — a body-only lang is silently ignored and exports come out
		// in English.
		const lang = i18n.language ? i18n.language : 'lt';
		const response: AxiosResponse<any> = yield call(
			api.post,
			`${path}/excel?lang=${encodeURIComponent(lang)}`,
			{
				...rest,
				lang,
				limit: 10000,
			},
			{ timeout: EXCEL_EXPORT_TIMEOUT_MS },
		);
		const { data, ...restResponse } = mapSuccessResponseToState(response);
		yield put(
			slice.getTableExcelSuccess({
				...restResponse,
				excel: {
					name: data.Name,
					type: data.Type,
					file: data.File,
					extension: data.Extension,
				},
			}),
		);
		yield delay(0);
		yield put(slice.getTableExcelFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, slice.getTableExcelFailure);
	}
}
